import { Directive, ElementRef, HostListener, Inject, Input, SimpleChanges } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { EventBus, EventBusBase } from '@skylab-angular-shared/core';
import { getFormContainerId } from '../utils/form';
import { UIEvents } from '../utils/ui-events';

@Directive()
export class ValueAccessor implements ControlValueAccessor {
    @Input() public readonly?: boolean;
    @Input() public disabled?: boolean;

    protected onChange: (value: any) => void = () => {};
    protected onTouched: () => void = () => {};

    protected formContainerId!: string;

    public currentValue: any;
    public previousValue: any;

    private isEdited = false;

    public constructor(
        protected elm: ElementRef,
        @Inject(EventBus) private readonly eventBus: EventBusBase
    ) {}

    public ngOnInit(): void {
        this.setReadonlyState();
        this.setDisabledState();
    }

    public ngOnDestroy?(): void;

    public ngAfterViewInit(): void {
        this.formContainerId = getFormContainerId(this.element as unknown as HTMLElement);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['readonly'] && !changes['readonly'].firstChange) {
            this.setReadonlyState();
            this.setDisabledState();
        }
        if (changes['disabled'] && !changes['disabled'].firstChange) {
            this.setDisabledState();
        }
    }

    protected get element(): HTMLInsInputElement {
        return this.elm.nativeElement;
    }

    public writeValue(value: any): void {
        this.element.value = this.currentValue = value;
    }

    public handleChangeEvent(elm: HTMLElement | HTMLInsInputElement, value: unknown): void {
        const newConverted = this.convertValue(value);

        if (elm === this.element && newConverted !== this.currentValue) {
            this.previousValue = this.currentValue;
            this.currentValue = newConverted;
            this.onChange(value);
            this.markDirty();
        }
    }

    protected convertValue(value: unknown): unknown {
        return value;
    }

    private markDirty(): void {
        if (!this.isEdited) {
            this.eventBus.emit(UIEvents.ControlEdited, this.element);
            this.isEdited = true;
        }
    }

    public registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setReadonlyState(): void {
        this.element.readonly = !!this.readonly;
    }

    public setDisabledState(): void {
        this.element.disabled = !!this.disabled || !!this.readonly;
    }

    @HostListener('insBlur', ['$event.target'])
    protected handleBlurEvent(elm: unknown): void {
        if (elm === this.element) {
            this.onTouched();
        }
    }
}
